import { appActions } from 'app/app.actions';
import { hasFeature } from 'assets/components/shared/featureFlag/hasFeature';
import { AppDispatch, AppState } from 'bootstrap.store';
// eslint-disable-next-line no-restricted-imports
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
// eslint-disable-next-line no-restricted-imports
import type { ComposeEmailModalFormValues } from 'components/shared/composeEmail/components/ComposeEmailModal';

import type { FormValues } from './composeEmailModal.types';

export function convertLegacyToV2InitialValues(
  legacy: ComposeEmailModalFormValues,
): Partial<FormValues> {
  const result: Partial<FormValues> = {};

  if (legacy.toUser?.length) {
    const first = legacy.toUser[0];
    result.toEmail = [
      {
        email: first.user.email || '',
        name: first.user.name ?? first.user.display_name ?? undefined,
      },
    ] as FormValues['toEmail'];
  }

  if (typeof legacy.fromEmails === 'string' && legacy.fromEmails) {
    result.fromEmail = [legacy.fromEmails];
  }
  if (legacy.linkConversationType) {
    result.linkType = legacy.linkConversationType;
  }
  if (legacy.parentConversation !== undefined) {
    result.parentConversation = legacy.parentConversation;
  }
  if (legacy.subject) {
    result.subject = legacy.subject;
  }
  if (legacy.message) {
    result.body = legacy.message;
  }
  if (legacy.internalNote) {
    result.internalNote = legacy.internalNote;
  }
  if (legacy.tags?.length) {
    result.tags = legacy.tags;
  }
  if (legacy.offer !== undefined) {
    result.offerInQueue = legacy.offer;
  }

  if (legacy.cc?.length) {
    result.cc = legacy.cc.map((u) => ({
      email: u.user.email || '',
      name: u.user.name ?? u.user.display_name ?? undefined,
    }));
  }

  if (legacy.bcc?.length) {
    result.bcc = legacy.bcc.map((u) => ({
      email: u.user.email || '',
      name: u.user.name ?? u.user.display_name ?? undefined,
    }));
  }

  return result;
}

/**
 * Returns the action to open the compose email modal, routing to the v2 or v1
 * modal based on the `email-compose-v2` feature flag.
 *
 * Accepts the legacy `ComposeEmailModalFormValues` payload; when routing to v2
 * the payload is converted to `Partial<FormValues>` automatically.
 *
 * Use this in sagas: `yield put(getOpenComposeEmailAction(state, payload))`.
 */
export function getOpenComposeEmailAction(
  state: AppState,
  payload?: { initialValues?: ComposeEmailModalFormValues },
) {
  if (hasFeature(state, 'email-compose-v2')) {
    const initialValues = payload?.initialValues
      ? convertLegacyToV2InitialValues(payload.initialValues)
      : undefined;
    return appActions.showModal.action({
      modal: { type: 'app:composeEmail', arg: { initialValues } },
    });
  }
  return composeEmailActions.composeEmailOpenModal(payload);
}

/**
 * Thunk version of {@link getOpenComposeEmailAction} for use in React
 * components.
 */
export const openComposeEmail =
  (payload?: { initialValues?: ComposeEmailModalFormValues }) =>
  (dispatch: AppDispatch, getState: () => AppState): void => {
    if (hasFeature(getState(), 'email-compose-v2')) {
      const initialValues = payload?.initialValues
        ? convertLegacyToV2InitialValues(payload.initialValues)
        : undefined;
      dispatch(
        appActions.showModal({
          modal: { type: 'app:composeEmail', arg: { initialValues } },
        }),
      );
    } else {
      dispatch(composeEmailActions.composeEmailOpenModal(payload));
    }
  };
